<template>
  <QsDrawer
    :title="drawerTitle"
    :visible.sync="drawerVisible"
    :before-close="handleBeforeClose"
    :destroy-on-close="true"
    :append-to-body="true"
    size="600px"
    custom-class="campaign-drawer"
  >
    <div v-loading="loading" class="campaign-drawer__body">
      <CampaignForm
        ref="formRef"
        v-model="formData"
        :mode="mode"
        :disabled="mode === 'view'"
        :campaign-id="campaignId"
        :campaign-status="campaignStatus"
        :initial-plans="initialPlans"
      />
    </div>

    <template #footer>
      <div class="drawer-footer">
        <el-button @click="handleCancel">取消</el-button>
        <el-button
          v-if="mode !== 'view'"
          type="primary"
          :loading="submitLoading"
          @click="handleSave"
        >
         保存
        </el-button>
      </div>
    </template>
  </QsDrawer>
</template>

<script>
import { saveCampaignSignCheck, getCampaignDetail } from '@/api/domain/activity'
import CampaignForm from '@/components/CampaignForm.vue'

export default {
  name: 'CampaignDrawer',

  components: {
    CampaignForm,
  },

  props: {
    /** Drawer visibility */
    visible: {
      type: Boolean,
      default: false,
    },
    /** Drawer mode: 'create' | 'edit' | 'view' */
    mode: {
      type: String,
      default: 'create',
      validator: (v) => ['create', 'edit', 'view'].includes(v),
    },
    /** Campaign ID (required for edit/view) */
    campaignId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      formData: {},
      campaignStatus: null,
      initialPlans: [],
      loading: false,
      submitLoading: false,
    }
  },

  computed: {
    drawerVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      },
    },
    drawerTitle() {
      const titles = {
        create: '新建活动',
        edit: '编辑活动',
        view: '活动详情',
      }
      return titles[this.mode] || '活动'
    },
  },

  watch: {
    visible(val) {
      if (val) {
        this.initDrawer()
      }
    },
  },

  methods: {
    /**
     * Initialize drawer content when opened.
     * For create mode: reset form to empty.
     * For edit/view mode: fetch campaign detail and populate form.
     */
    async initDrawer() {
      if (this.mode === 'create') {
        this.formData = {}
        this.campaignStatus = null
        this.initialPlans = []
        this.$nextTick(() => {
          this.$refs.formRef?.resetFields()
        })
        return
      }

      // Edit or view mode: load detail
      if (!this.campaignId) {
        this.$message.error('活动ID不能为空')
        this.drawerVisible = false
        return
      }

      this.loading = true
      const { code, data } = await getCampaignDetail({ id: this.campaignId })
      if (code === 1 && data) {
        this.campaignStatus = data.activityState
        this.initialPlans = (data.planList || []).map((p) => ({
          id: p.planId,
          name: p.planName,
        }))
        this.formData = this.mapDetailToForm(data)
      } else {
        this.$message.error('获取活动详情失败')
        this.drawerVisible = false
      }

      this.loading = false
    },

    mapDetailToForm(detail) {
      return {
        campaignName: detail.activityName || '',
        dateRange: [detail.startDate || '', detail.endDate || ''],
        preFreeRentDays: detail.preFreeRentDays ?? 0,
        fullRentGiftDays: detail.presentDays ?? 0,
        bindPlanIds:
          detail.planList?.map((p) => p.planId).filter(Boolean) || [],
      }
    },

    async handleSave() {
      const form = this.$refs.formRef
      if (!form) return

      await form.validate()

      const params = form.buildSaveParams()
      this.submitLoading = true
      const { code } = await saveCampaignSignCheck(params)
      this.submitLoading = false
      if (code === 1) {
        this.$message.success(
          this.mode === 'create' ? '活动创建成功' : '活动编辑成功',
        )
        this.$emit('saved')
        this.drawerVisible = false
      }
    },

    handleBeforeClose(done) {
      if (this.mode === 'view') {
        done()
        return
      }
      this.$confirm('确定取消吗？已填写的内容将不会被保存。', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        done()
      }).catch(() => {
        // 用户取消 - drawer stays open
      })
    },

    /**
     * Handle cancel button click.
     */
    handleCancel() {
      this.handleBeforeClose(() => {
        this.drawerVisible = false
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.campaign-drawer__body {
  padding: 0 16px;
  min-height: 300px;
}

.drawer-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
</style>
